<script setup lang="ts">
import { computed, inject } from 'vue';
import { useRoute } from 'vue-router';
import type { MenuItem } from '@/types/routing-types';

const props = withDefaults(defineProps<{
  item: MenuItem;
  isCollapsed: boolean;
  level?: number
}>(), {
  level: 0
});

const route = useRoute();
const closeOffcanvas = inject<() => void>('closeOffcanvas');

function queryMatches(itemQuery: Record<string, string>, routeQuery: Record<string, any>): boolean {
  return Object.entries(itemQuery).every(([k, v]) => routeQuery[k] === v);
}

function itemMatchesRoute(item: MenuItem, currentPath: string, currentQuery: Record<string, any>): boolean {
  if (!item.path) return false;
  if (item.query) {
    return currentPath === item.path && queryMatches(item.query, currentQuery);
  }
  if (item.exact) return currentPath === item.path;
  return currentPath === item.path || currentPath.startsWith(item.path + '/');
}

function descendantMatches(node: MenuItem, currentPath: string, currentQuery: Record<string, any>): boolean {
  if (itemMatchesRoute(node, currentPath, currentQuery)) return true;
  return (node.children ?? []).some((c) => descendantMatches(c, currentPath, currentQuery));
}

// Check if current route matches this item
const isActive = computed(() => {
  if (itemMatchesRoute(props.item, route.path, route.query)) return true;
  if (props.item.children && props.item.children.length > 0) {
    return props.item.children.some((c) => descendantMatches(c, route.path, route.query));
  }
  return false;
});

const linkTo = computed(() => {
  return props.item.query
    ? { path: props.item.path, query: props.item.query }
    : props.item.path;
});

// Per-level indent applied directly to the nav-link so the active highlight
// extends across the full width. Bootstrap's default ps-* scale stops at ps-5
// (3rem) and doesn't differentiate nested levels visually, so we use rem here.
const indentStyle = computed(() => {
  if (props.isCollapsed) return undefined;
  if (props.level <= 0) return undefined;
  return { paddingLeft: `${props.level * 1.25 + 0.5}rem` };
});

// Inject collapse state from parent
const collapseState = inject<{
  isExpanded: (id: string) => boolean;
  toggleCollapse: (id: string) => void;
}>('collapseState');

// Track submenu expansion (reactive state from parent)
const isSubmenuExpanded = computed(() => {
  return collapseState?.isExpanded(`submenu-${String(props.item.name)}`) || false;
});

// Toggle collapse (reactive)
function toggleCollapse(menuId: string) {
  collapseState?.toggleCollapse(menuId);
}

function onNavClick() {
  closeOffcanvas?.();
}
</script>

<template>

    <!-- Simple item (no children) -->
    <template v-if="!item.children || item.children.length === 0">
      <li class="nav-item">
      <router-link
        :to="linkTo"
        class="nav-link"
        active-class=""
        exact-active-class=""
        :class="{ 'active': isActive, 'd-flex align-items-center': !isCollapsed }"
        :style="indentStyle"
        :aria-current="isActive ? 'page' : undefined"
        :title="item.title"
        :data-bs-toggle="isCollapsed ? 'tooltip' : ''"
        @click="onNavClick"
      >
        <i :class="[item.icon, { 'nav-icon-collapsed': isCollapsed }]" class="mx-3"></i>
        <span v-if="!isCollapsed">{{ item.title }}</span>
      </router-link>
      </li>
    </template>

    <!-- Item with children (submenu) -->
    <template v-else>
      <div class="nav-item">
        <router-link
          :to="linkTo"
          class="nav-link"
          active-class=""
          exact-active-class=""
          :class="{ 'active': isActive, 'd-flex align-items-center' : !isCollapsed }"
          :style="indentStyle"
          :aria-current="isActive ? 'page' : undefined"
          :title="item.title"
          :data-bs-toggle="isCollapsed ? 'tooltip' : ''"
          @click="onNavClick"
        >
          <i :class="[item.icon, { 'nav-icon-collapsed': isCollapsed }]" class="mx-3"></i>
          <span v-if="!isCollapsed">{{ item.title }}</span>
          <span
            v-if="!isCollapsed"
            class="ms-auto pe-2"
            role="button"
            :aria-expanded="isSubmenuExpanded"
            :aria-controls="`submenu-${String(item.name)}`"
            @click.stop.prevent="toggleCollapse(`submenu-${String(item.name)}`)"
          >
            <i
              class="chev-icon bi bi-chevron-down"
              :class="{ 'rotate-180': isSubmenuExpanded }"
            ></i>
          </span>
        </router-link>
      </div>

      <!-- Submenu -->
      <ul
        v-if="!isCollapsed"
        class="navbar-nav flex-column collapse"
        :class="{ 'show': isSubmenuExpanded }"
        :id="`submenu-${String(item.name)}`"
      >
        <nav-bar-item
          v-for="child in item.children"
          :key="child.path || String(child.name)"
          :item="child"
          :is-collapsed="isCollapsed"
          :level="(level || 0) + 1"
        />
      </ul>
    </template>
</template>

<style lang="scss" scoped>

.nav-link:hover, .nav-link.active {
  background-color: var(--bs-secondary-bg);
  padding-right: -2px;
}

.navbar-nav {
  .chev-icon {
    transition: transform 0.3s ease;
    transform-origin: center;
    display: inline-block;

    &.rotate-180 {
      transform: rotate(180deg);
    }
  }
}


.nav-icon-collapsed {
  //font-size: 1.5em !important;
}
</style>
